/**
 * VideoPostTranscriptManager Component
 * 
 * Advanced transcript management interface for video posts featuring:
 * - Video post metadata display with beautiful header design
 * - Transcript list with timing information
 * - Add, edit, reorder, and delete transcript functionality
 * - Integration with translation system
 * - Drag-and-drop reordering functionality
 * - Real-time progress tracking and status updates
 * 
 * Used in: App.js route /video-posts/edit/:id
 */
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Alert,
    Chip,
    Paper,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Add,
    ArrowBack,
    AccessTime,
    Psychology,
    ExpandMore,
    ExpandLess,
    Sort
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import PageContainer from '../layout/PageContainer';
import VideoPlayerWidget from './VideoPlayerWidget';
import NLPTranscriptDialog from './NLPTranscriptDialog';
import ShiftDialog from './ShiftDialog';
import TranscriptDialog from './TranscriptDialog';
import TranscriptGroup from './TranscriptGroup';


// Helper function to group transcripts by their all_texts entry
const groupTranscriptsByAllText = (transcripts, allTexts) => {
    if (!transcripts || !allTexts) return [];
    
    const groups = [];
    
    allTexts.forEach(allText => {
        const groupTranscripts = transcripts.filter(transcript => 
            transcript.start >= allText.start_seconds && 
            transcript.end <= allText.end_seconds
        ).sort((a, b) => a.index - b.index);
        
        if (groupTranscripts.length > 0) {
            groups.push({
                allText,
                transcripts: groupTranscripts,
                combinedTextContent: allText.text?.text || ''
            });
        }
    });
    
    return groups;
};

const VideoPostTranscriptManager = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [post, setPost] = useState(null);
    const [transcripts, setTranscripts] = useState([]);
    const [groupedTranscripts, setGroupedTranscripts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
    
    // Transcript editing states
    const [transcriptDialog, setTranscriptDialog] = useState({ 
        open: false, 
        transcript: null, 
        isEdit: false,
        targetGroup: null
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTranscript, setSelectedTranscript] = useState(null);
    
    // Video player state
    const [isVideoPlayerExpanded, setIsVideoPlayerExpanded] = useState(false);
    
    // Original transcript expandable state
    const [isOriginalTranscriptExpanded, setIsOriginalTranscriptExpanded] = useState(false);
    
    // NLP dialog state
    const [nlpDialogOpen, setNlpDialogOpen] = useState(false);
    
    // Shift dialog state
    const [shiftDialog, setShiftDialog] = useState({
        open: false,
        group: null
    });

    // Handle unhandled promise rejections (like AbortError from video play/pause)
    useEffect(() => {
        const handleUnhandledRejection = (event) => {
            if (event.reason && event.reason.name === 'AbortError') {
                // Ignore AbortError from video play/pause interruptions
                event.preventDefault();
                console.log('Ignored AbortError:', event.reason.message);
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        
        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    const showAlert = useCallback((message, severity = 'info') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
    }, []);

    const fetchPostAndTranscripts = useCallback(async () => {
        setLoading(true);
        try {
            const postResponse = await userRequest.get(`/contentVideoPost/${id}`);
            if (postResponse?.data) {
                const postData = postResponse.data;
                setPost(postData);
                
                // Add textContent for video player captions
                const transcriptsWithContent = (postData.transcripts || []).map(transcript => {
                    const allText = postData.all_texts?.find(at => 
                        transcript.start >= at.start_seconds && 
                        transcript.end <= at.end_seconds
                    );
                    
                    let textContent = '';
                    if (allText?.text?.text && transcript.positions) {
                        textContent = allText.text.text.substring(
                            transcript.positions.left, 
                            transcript.positions.right
                        );
                    }
                    
                    return {
                        ...transcript,
                        textContent // Only for video player captions
                    };
                });
                
                setTranscripts(transcriptsWithContent);
            }
        } catch (error) {
            console.error('Error loading video post:', error);
            showAlert('Error loading video post: ' + (error?.response?.data?.error || error.message), 'error');
            setPost(null);
            setTranscripts([]);
        } finally {
            setLoading(false);
        }
    }, [id, showAlert]);

    // Trigger initial data fetch when component mounts or id changes
    useEffect(() => {
        if (id) {
            fetchPostAndTranscripts();
        }
    }, [id, fetchPostAndTranscripts]);

    // Group transcripts when post or transcripts change
    useEffect(() => {
        if (post && transcripts.length > 0 && post.all_texts) {
            const grouped = groupTranscriptsByAllText(transcripts, post.all_texts);
            setGroupedTranscripts(grouped);
        } else {
            setGroupedTranscripts([]);
        }
    }, [post, transcripts]);

    const handleAddTextGroup = async () => {
        try {
            // Create a new text object
            const textResponse = await userRequest.post('/text', {
                text: 'New text group',
                language: post.language._id
            });

            const newTextId = textResponse.data._id;

            // Determine timing for the new group (after the last existing group)
            const lastGroup = groupedTranscripts[groupedTranscripts.length - 1];
            const startTime = lastGroup ? lastGroup.allText.end_seconds + 1 : 0;
            const endTime = startTime + 10; // Default 10 second duration

            // Create a new all_texts entry
            await userRequest.post(`/contentVideoPost/${id}/all_texts`, {
                start_seconds: startTime,
                end_seconds: endTime,
                text: newTextId
            });

            showAlert('New text group created successfully', 'success');
            fetchPostAndTranscripts(); // Refresh to show new group
        } catch (error) {
            showAlert('Error creating text group: ' + error.message, 'error');
        }
    };

    const handleAddTranscriptToGroup = (group) => {
        setTranscriptDialog({ 
            open: true, 
            transcript: null, 
            isEdit: false,
            targetGroup: group
        });
    };

    const handleEditTranscript = (transcript) => {
        setTranscriptDialog({ 
            open: true, 
            transcript, 
            isEdit: true 
        });
    };

    const handleDeleteTranscript = (transcript) => {
        console.log('handleDeleteTranscript', transcript);
        setSelectedTranscript(transcript);
        setDeleteDialogOpen(true);
    };

    const handleTranslateTranscript = (textObject) => {
        // Called from text group level with direct text ID
        if (textObject?.text?._id) {
            // Navigate to translation editor
            navigate(`/text/translate/${textObject.text._id}`);
        } else {
            showAlert('Cannot open translation editor: Text ID not found', 'error');
        }
    };

    const confirmDeleteTranscript = async () => {
        try {
            await userRequest.delete(`/contentVideoPost/${id}/transcripts/${selectedTranscript._id}`);
            showAlert('Transcript deleted successfully', 'success');
            fetchPostAndTranscripts();
        } catch (error) {
            showAlert('Error deleting transcript: ' + error.message, 'error');
        } finally {
            setDeleteDialogOpen(false);
            setSelectedTranscript(null);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleNLPStatusUpdate = (statusUpdate) => {
        if (statusUpdate.shouldRefresh) {
            // Refresh the entire video post data
            fetchPostAndTranscripts();
            showAlert('Video post data refreshed', 'info');
        } else {
            // Just update the post state with provided updates
            setPost(prevPost => ({
                ...prevPost,
                ...statusUpdate
            }));
            showAlert('NLP status updated', 'info');
        }
    };

    const handleAutoIndex = async () => {
        if (!window.confirm('This will reorder all transcripts based on their start time. Continue?')) {
            return;
        }

        try {
            await userRequest.patch(`/contentVideoPost/${id}/transcripts/auto-index`);
            showAlert('Transcripts reordered successfully', 'success');
            fetchPostAndTranscripts();
        } catch (error) {
            showAlert('Error reordering transcripts: ' + error.message, 'error');
        }
    };

    const handleReadyToServeToggle = async (event) => {
        const newValue = event.target.checked;
        
        try {
            await userRequest.patch(`/contentVideoPost/${id}`, {
                isReadyToServe: newValue
            });
            
            setPost(prevPost => ({
                ...prevPost,
                isReadyToServe: newValue
            }));
            
            showAlert(
                newValue ? 'Video post published successfully' : 'Video post unpublished successfully', 
                'success'
            );
        } catch (error) {
            showAlert('Error updating publish status: ' + error.message, 'error');
        }
    };

    const handleShiftGroup = (group) => {
        setShiftDialog({
            open: true,
            group
        });
    };



    if (loading) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <Typography>Loading video post...</Typography>
                </Box>
            </PageContainer>
        );
    }

    if (!post) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <Box textAlign="center">
                        <Typography variant="h6" gutterBottom>Video post not found</Typography>
                        <Button onClick={() => navigate('/video-posts')} variant="outlined">
                            Back to Video Posts
                        </Button>
                    </Box>
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/video-posts')}
                        sx={{ mr: 2 }}
                    >
                        Back to Video Posts
                    </Button>
                </Box>

                {/* Video Post Header */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                                {post.title}
                            </Typography>
                            
                            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                                <Chip 
                                    label={post.isReadyToServe ? "Ready" : "Draft"} 
                                    color={post.isReadyToServe ? "success" : "warning"}
                                    variant="outlined"
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {post.language?.name}
                                </Typography>
                                {post.author && (
                                    <Typography variant="body2" color="text.secondary">
                                        by {post.author}
                                    </Typography>
                                )}
                            </Box>

                            {/* Original Transcript Expandable Section */}
                            {post.originalTranscript && (
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <Button
                                        variant="text"
                                        onClick={() => setIsOriginalTranscriptExpanded(!isOriginalTranscriptExpanded)}
                                        startIcon={isOriginalTranscriptExpanded ? <ExpandLess /> : <ExpandMore />}
                                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                                    >
                                        {isOriginalTranscriptExpanded ? 'Hide' : 'Show'} Original Transcript
                                    </Button>
                                    
                                    {isOriginalTranscriptExpanded && (
                                        <Paper sx={{ p: 2, mt: 1, backgroundColor: 'grey.50' }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                                {post.originalTranscript}
                                            </Typography>
                                        </Paper>
                                    )}
                                </Box>
                            )}

                            <Box display="flex" alignItems="center" gap={2}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="body2" color="text.secondary">
                                        {transcripts.length} transcripts
                                    </Typography>
                                </Box>
                                {post.estimatedWatchTime > 0 && (
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <AccessTime fontSize="small" sx={{ color: 'text.secondary' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {post.estimatedWatchTime}m
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
                                {/* Publish/Unpublish Toggle */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={post.isReadyToServe || false}
                                            onChange={handleReadyToServeToggle}
                                            color="success"
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2">
                                                {post.isReadyToServe ? 'Published' : 'Draft'}
                                            </Typography>
                                        </Box>
                                    }
                                    labelPlacement="start"
                                    sx={{ 
                                        margin: 0,
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.875rem',
                                            fontWeight: 500
                                        }
                                    }}
                                />
                                
                                <Button
                                    variant="outlined"
                                    startIcon={<Psychology />}
                                    onClick={() => setNlpDialogOpen(true)}
                                    sx={{ borderRadius: 2 }}
                                >
                                    NLP Transcribe
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Video Player Widget - Full Width When Expanded */}
                <Suspense fallback={<Typography variant="body2">Loading video player...</Typography>}>
                    <VideoPlayerWidget
                        post={post}
                        transcripts={transcripts}
                        isExpanded={isVideoPlayerExpanded}
                        onToggleExpanded={() => setIsVideoPlayerExpanded(!isVideoPlayerExpanded)}
                        onEditTranscript={handleEditTranscript}
                    />
                </Suspense>

                {alert.show && (
                    <Alert severity={alert.severity} sx={{ mb: 2 }}>
                        {alert.message}
                    </Alert>
                )}
            </Box>


            <Box display="flex" gap={2} sx={{ mb: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleAddTextGroup}
                    sx={{ borderRadius: 2 }}
                    startIcon={<Add />}
                >
                    Add Text Group
                </Button>
                
                {transcripts.length > 1 && (
                    <Button
                        variant="outlined"
                        onClick={handleAutoIndex}
                        sx={{ borderRadius: 2 }}
                        startIcon={<Sort />}
                    >
                        Auto-Index by Time
                    </Button>
                )}
            </Box>

            {/* Transcripts List */}
            {transcripts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        No text groups yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Start by creating text groups, then add transcript segments within each group.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddTextGroup}
                        sx={{ borderRadius: 2 }}
                    >
                        Add First Text Group
                    </Button>
                </Box>
            ) : (
                <Box sx={{ gap: 2 }}>
                    {groupedTranscripts.map((group, groupIndex) => (
                        <TranscriptGroup
                            key={group.allText._id}
                            group={group}
                            groupIndex={groupIndex}
                            onEdit={handleEditTranscript}
                            onDelete={handleDeleteTranscript}
                            onTranslate={handleTranslateTranscript}
                            onShift={handleShiftGroup}
                            onAddTranscript={handleAddTranscriptToGroup}
                            formatTime={formatTime}
                        />
                    ))}
                </Box>
            )}

            {/* Add/Edit Transcript Dialog */}
            <TranscriptDialog
                open={transcriptDialog.open}
                transcript={transcriptDialog.transcript}
                isEdit={transcriptDialog.isEdit}
                onClose={() => setTranscriptDialog({ open: false, transcript: null, isEdit: false, targetGroup: null })}
                onSave={fetchPostAndTranscripts}
                onUpdateTranscripts={setTranscripts}
                onUpdatePost={setPost}
                post={post}
                targetGroup={transcriptDialog.targetGroup}
                postId={id}
                showAlert={showAlert}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Transcript</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this transcript? 
                        This will also delete any associated translations. 
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDeleteTranscript} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* NLP Transcript Dialog */}
            <NLPTranscriptDialog
                open={nlpDialogOpen}
                onClose={() => setNlpDialogOpen(false)}
                videoPost={post}
                onStatusUpdate={handleNLPStatusUpdate}
            />

            {/* Shift Dialog */}
            <ShiftDialog
                open={shiftDialog.open}
                group={shiftDialog.group}
                onClose={() => setShiftDialog({ open: false, group: null })}
                onApply={fetchPostAndTranscripts}
                postId={id}
                showAlert={showAlert}
            />
        </PageContainer>
    );
};

export default VideoPostTranscriptManager; 